import React, { useState, useMemo, useCallback } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import {
  Building2, HandCoins, Percent, SlidersHorizontal, Scale,
  Download, Save, FolderOpen, Trash2, X, RotateCcw,
  Calculator as CalcIcon, Info
} from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Slider } from '../components/ui/slider';
import { formatCurrency, formatPercentage } from '../utils/formatters';
import { ConstructionSizerPDF } from './PDFDocument';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pctOfTotal = (n, total) =>
  total > 0 ? formatPercentage((n / total) * 100, 1) : '—';

// ── Field components (match app-wide input styling) ──────────────────────────

const FieldLabel = ({ children, hint }) => (
  <Label className="text-xs text-[#637888] uppercase tracking-wide flex items-center gap-1.5">
    {children}
    {hint && (
      <span title={hint} className="cursor-help text-slate-300 hover:text-slate-400 transition-colors">
        <Info size={11} />
      </span>
    )}
  </Label>
);

const CurrencyField = ({ label, hint, value, onChange, testid }) => {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState('');
  const display = focused
    ? raw
    : (value === 0 || value === '' || value === undefined ? '' : Number(value).toLocaleString('en-US'));
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="relative mt-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">$</span>
        <Input
          type="text"
          inputMode="decimal"
          value={display}
          placeholder="0"
          onFocus={() => { setFocused(true); setRaw(value === 0 ? '' : String(value)); }}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const r = e.target.value.replace(/[^0-9.]/g, '');
            setRaw(r);
            onChange(Math.max(0, parseFloat(r) || 0));
          }}
          data-testid={testid}
          className="pl-7 font-mono-number"
        />
      </div>
    </div>
  );
};

const PctField = ({ label, hint, value, onChange, min = 0, max = 100, step = 0.1, testid }) => (
  <div>
    {label && <FieldLabel hint={hint}>{label}</FieldLabel>}
    <div className={`relative ${label ? 'mt-1' : ''}`}>
      <Input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => {
          const v = parseFloat(e.target.value) || 0;
          onChange(Math.min(max, Math.max(min, v)));
        }}
        data-testid={testid}
        className="pr-7 font-mono-number"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">%</span>
    </div>
  </div>
);

const MonthField = ({ label, hint, value, onChange, testid }) => (
  <div>
    <FieldLabel hint={hint}>{label}</FieldLabel>
    <div className="relative mt-1">
      <Input
        type="number"
        min={1}
        max={60}
        step={1}
        value={value}
        onChange={(e) => onChange(Math.max(1, parseInt(e.target.value) || 1))}
        data-testid={testid}
        className="pr-9 font-mono-number"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#637888] font-mono">mo</span>
    </div>
  </div>
);

const MetricBox = ({ label, value, sub, testid }) => (
  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
    <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">{label}</p>
    <p className="font-mono-number text-base font-semibold text-[#002F87]" data-testid={testid}>
      {value}
    </p>
    {sub && <p className="text-xs text-[#637888] mt-0.5">{sub}</p>}
  </div>
);

const ConstraintSlider = ({ label, hint, value, onChange, min, max, step, testid }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between gap-4">
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="w-24 flex-shrink-0">
        <PctField value={value} onChange={onChange} min={min} max={max} step={step} testid={testid} />
      </div>
    </div>
    <Slider
      value={[value]}
      onValueChange={(v) => onChange(v[0])}
      min={min}
      max={max}
      step={step}
      data-testid={`${testid}-slider`}
      className="w-full"
    />
    <div className="flex justify-between text-xs text-[#637888] font-mono-number">
      <span>{min}%</span>
      <span>{max}%</span>
    </div>
  </div>
);

// ── Core Calculation ──────────────────────────────────────────────────────────

export function calculateSizer(inputs) {
  const {
    landCost, hardCosts, softCosts, arv,
    annualRate, termMonths, closingCostRate, irCoverage,
    maxLTV, maxLTC,
    landValue, currentDebt, midpointPct,
  } = inputs;

  const baseCosts = landCost + hardCosts + softCosts;
  const monthlyRate = annualRate / 100 / 12;
  const irMonths = termMonths * (irCoverage / 100);

  // k = (closing cost % + IR months × monthly rate) expressed as decimal fraction of loan
  const k = (closingCostRate / 100) + (monthlyRate * irMonths);

  // Max loan by ARV
  const maxByLTV = arv * (maxLTV / 100);

  // Max loan by LTC — solved algebraically so CC & IR are inside the denominator
  const denom = 1 - k * (maxLTC / 100);
  const maxByLTC = denom > 0.001
    ? baseCosts * (maxLTC / 100) / denom
    : Number.POSITIVE_INFINITY;

  const loanAmount = Math.max(
    0,
    Math.min(maxByLTV, isFinite(maxByLTC) ? maxByLTC : maxByLTV)
  );

  const bindingConstraint = maxByLTV <= (isFinite(maxByLTC) ? maxByLTC : Infinity) ? 'LTV' : 'LTC';

  const closingCosts = loanAmount * (closingCostRate / 100);
  const monthlyIO = loanAmount * monthlyRate;
  const interestReserve = monthlyIO * irMonths;
  const totalInterestFullTerm = monthlyIO * termMonths;

  const totalProjectCost = baseCosts + closingCosts + interestReserve;
  const equity = Math.max(0, totalProjectCost - loanAmount);

  const actualLTC = totalProjectCost > 0 ? (loanAmount / totalProjectCost) * 100 : 0;
  const actualLTV = arv > 0 ? (loanAmount / arv) * 100 : 0;

  // Initial Funding: Gross = Land Value × Midpoint%; Net = Gross − Current Debt; capped at loan
  const grossInitialFunding = landValue * (midpointPct / 100);
  const initialFunding = Math.min(loanAmount, Math.max(0, grossInitialFunding - currentDebt));
  const constructionHoldback = Math.max(0, loanAmount - initialFunding);

  return {
    baseCosts, loanAmount, bindingConstraint,
    maxByLTV,
    maxByLTC: isFinite(maxByLTC) ? maxByLTC : 0,
    closingCosts, monthlyIO, interestReserve,
    totalInterestFullTerm, totalProjectCost, equity,
    actualLTC, actualLTV, irMonths,
    grossInitialFunding, initialFunding, constructionHoldback,
  };
}

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULT_INPUTS = {
  projectName: '',
  propertyAddress: '',
  landCost: 200000,
  hardCosts: 400000,
  softCosts: 40000,
  arv: 900000,
  annualRate: 9.5,
  termMonths: 12,
  closingCostRate: 4.0,
  irCoverage: 60.0,
  maxLTV: 55.0,
  maxLTC: 70.0,
  landValue: 200000,
  currentDebt: 0,
  midpointPct: 62.5,
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function LoanSizer({ loanOfficer, controlsRef }) {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [showSave, setShowSave] = useState(false);
  const [showLoad, setShowLoad] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [toast, setToast] = useState(null);

  const update = useCallback((key) => (val) => setInputs(prev => ({ ...prev, [key]: val })), []);

  const c = useMemo(() => calculateSizer(inputs), [inputs]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const toSnakeCase = (inp) => ({
    project_name: inp.projectName,
    property_address: inp.propertyAddress,
    land_cost: inp.landCost,
    hard_costs: inp.hardCosts,
    soft_costs: inp.softCosts,
    arv: inp.arv,
    annual_rate: inp.annualRate,
    term_months: inp.termMonths,
    closing_cost_rate: inp.closingCostRate,
    ir_coverage: inp.irCoverage,
    max_ltv: inp.maxLTV,
    max_ltc: inp.maxLTC,
    land_value: inp.landValue,
    current_debt: inp.currentDebt,
    midpoint_pct: inp.midpointPct,
  });

  const handleSave = async () => {
    if (!scenarioName.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/sizer-scenarios`, { name: scenarioName.trim(), officer_id: loanOfficer?.id || 'unknown', ...toSnakeCase(inputs) });
      showToast('Scenario saved successfully');
      setShowSave(false);
      setScenarioName('');
    } catch {
      showToast('Failed to save scenario', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadOpen = async () => {
    setLoadingScenarios(true);
    setShowLoad(true);
    try {
      const { data } = await axios.get(`${API}/sizer-scenarios`, { params: loanOfficer?.id ? { officer_id: loanOfficer.id } : {} });
      setSavedScenarios(data);
    } catch {
      showToast('Failed to load scenarios', 'error');
    } finally {
      setLoadingScenarios(false);
    }
  };

  React.useEffect(() => {
    if (controlsRef) controlsRef.current = { openSave: () => setShowSave(true), openLoad: handleLoadOpen };
  });

  const handleLoadScenario = (s) => {
    setInputs({
      projectName: s.project_name || '',
      propertyAddress: s.property_address || '',
      landCost: s.land_cost,
      hardCosts: s.hard_costs,
      softCosts: s.soft_costs,
      arv: s.arv,
      annualRate: s.annual_rate,
      termMonths: s.term_months,
      closingCostRate: s.closing_cost_rate,
      irCoverage: s.ir_coverage,
      maxLTV: s.max_ltv,
      maxLTC: s.max_ltc,
      landValue: s.land_value ?? s.land_cost,
      currentDebt: s.current_debt ?? 0,
      midpointPct: s.midpoint_pct ?? ((s.max_ltv + s.max_ltc) / 2),
    });
    setShowLoad(false);
    showToast(`Loaded: ${s.name}`);
  };

  const handleDeleteScenario = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/sizer-scenarios/${id}`);
      setSavedScenarios(prev => prev.filter(s => s.id !== id));
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    showToast('Inputs reset to defaults');
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded shadow-lg text-sm font-medium transition-all ${
            toast.type === 'error' ? 'bg-red-700 text-white' : 'bg-[#002F87] text-white'
          }`}
          data-testid="toast-message"
        >
          {toast.msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

        {/* Input Sections — Left Column */}
        <div className="lg:col-span-3 space-y-6">

          {/* PROJECT INPUTS */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between text-[#002F87] font-heading text-sm uppercase tracking-wide">
                <span className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" strokeWidth={1.5} />
                  Project Inputs
                </span>
                <span className="text-xs font-normal normal-case tracking-normal bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-mono-number">
                  Single Family
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Project Name</FieldLabel>
                  <Input
                    type="text"
                    value={inputs.projectName}
                    onChange={(e) => update('projectName')(e.target.value)}
                    placeholder="e.g., Oak Street Spec Build"
                    data-testid="project-name-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <FieldLabel>Property Address</FieldLabel>
                  <Input
                    type="text"
                    value={inputs.propertyAddress}
                    onChange={(e) => update('propertyAddress')(e.target.value)}
                    placeholder="e.g., 123 Oak St, Miami FL"
                    data-testid="property-address-input"
                    className="mt-1"
                  />
                </div>
                <CurrencyField
                  label="Land / Lot Acquisition"
                  value={inputs.landCost}
                  onChange={update('landCost')}
                  testid="land-cost-input"
                />
                <CurrencyField
                  label="ARV — After Renovation Value"
                  hint="Estimated market value upon project completion"
                  value={inputs.arv}
                  onChange={update('arv')}
                  testid="arv-input"
                />
                <CurrencyField
                  label="Hard Construction Costs"
                  value={inputs.hardCosts}
                  onChange={update('hardCosts')}
                  testid="hard-costs-input"
                />
                <CurrencyField
                  label="Soft Costs"
                  hint="Permits, architecture, engineering, inspections"
                  value={inputs.softCosts}
                  onChange={update('softCosts')}
                  testid="soft-costs-input"
                />
              </div>
              <div className="mt-4 p-4 bg-slate-50 rounded-md border border-slate-200" data-testid="base-costs-summary">
                <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Base Project Cost</p>
                <p className="font-mono-number text-2xl font-bold text-[#002F87]">{formatCurrency(c.baseCosts)}</p>
                <p className="text-xs text-[#637888] mt-1">Excl. closing costs &amp; interest reserve</p>
              </div>
            </CardContent>
          </Card>

          {/* INITIAL FUNDING */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                <HandCoins className="w-4 h-4" strokeWidth={1.5} />
                Initial Funding at Closing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CurrencyField
                  label="Land Value (for lending)"
                  hint="Appraised or agreed land value used to calculate the initial advance — may differ from acquisition cost"
                  value={inputs.landValue}
                  onChange={update('landValue')}
                  testid="land-value-input"
                />
                <CurrencyField
                  label="Current Debt on Land"
                  hint="Existing lien or mortgage on the land that will be paid off at closing from the initial advance"
                  value={inputs.currentDebt}
                  onChange={update('currentDebt')}
                  testid="current-debt-input"
                />
                <div className="md:col-span-2">
                  <FieldLabel hint="LTV/LTC midpoint used to size the land advance — e.g., if LTV=55% and LTC=70%, midpoint=62.5%">
                    Initial Funding Midpoint %
                  </FieldLabel>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1">
                      <PctField
                        value={inputs.midpointPct}
                        onChange={update('midpointPct')}
                        min={0}
                        max={100}
                        step={0.5}
                        testid="midpoint-pct-input"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => update('midpointPct')((inputs.maxLTV + inputs.maxLTC) / 2)}
                      data-testid="sync-midpoint-btn"
                      title={`Auto-set to (LTV + LTC) ÷ 2 = ${((inputs.maxLTV + inputs.maxLTC) / 2).toFixed(1)}%`}
                      className="flex-shrink-0 text-xs text-[#637888] hover:text-[#002F87] whitespace-nowrap"
                    >
                      Sync avg ({((inputs.maxLTV + inputs.maxLTC) / 2).toFixed(1)}%)
                    </Button>
                  </div>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <MetricBox
                  label="Gross Land Advance"
                  value={formatCurrency(c.grossInitialFunding)}
                  sub={`${formatCurrency(inputs.landValue)} × ${formatPercentage(inputs.midpointPct, 2)}`}
                  testid="gross-initial-funding"
                />
                <div className="p-3 bg-[#002F87] rounded-md">
                  <p className="text-xs text-white/80 uppercase tracking-wide mb-1">Initial Funding</p>
                  <p className="font-mono-number text-base font-bold text-white" data-testid="initial-funding-result">
                    {formatCurrency(c.initialFunding)}
                  </p>
                  <p className="text-xs text-white/60 mt-0.5">Gross − {formatCurrency(inputs.currentDebt)} debt</p>
                </div>
                <MetricBox
                  label="Construction Holdback"
                  value={formatCurrency(c.constructionHoldback)}
                  sub="Total Loan − Initial Funding"
                  testid="construction-holdback-result"
                />
              </div>
            </CardContent>
          </Card>

          {/* LOAN TERMS */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                <Percent className="w-4 h-4" strokeWidth={1.5} />
                Loan Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PctField
                  label="Interest Rate (Annual)"
                  hint="Annual nominal interest rate — used for IO payment calculation"
                  value={inputs.annualRate}
                  onChange={update('annualRate')}
                  min={0}
                  max={30}
                  step={0.1}
                  testid="annual-rate-input"
                />
                <MonthField
                  label="Loan Term"
                  value={inputs.termMonths}
                  onChange={update('termMonths')}
                  testid="term-months-input"
                />
                <PctField
                  label="Closing Costs"
                  hint="As a percentage of loan amount — default 4%"
                  value={inputs.closingCostRate}
                  onChange={update('closingCostRate')}
                  min={0}
                  max={20}
                  step={0.25}
                  testid="closing-cost-rate-input"
                />
                <PctField
                  label="Interest Reserve Coverage"
                  hint="Percentage of loan term funded by interest reserve — default 60%"
                  value={inputs.irCoverage}
                  onChange={update('irCoverage')}
                  min={0}
                  max={100}
                  step={5}
                  testid="ir-coverage-input"
                />
              </div>

              <Separator className="my-4" />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <MetricBox
                  label="Monthly IO Payment"
                  value={formatCurrency(c.monthlyIO)}
                  sub={`${formatPercentage(inputs.annualRate, 2)} ÷ 12`}
                  testid="monthly-io-summary"
                />
                <MetricBox
                  label="Interest Reserve"
                  value={formatCurrency(c.interestReserve)}
                  sub={`${c.irMonths.toFixed(1)} mo covered`}
                  testid="ir-summary"
                />
                <MetricBox
                  label="Closing Costs"
                  value={formatCurrency(c.closingCosts)}
                  sub={`${formatPercentage(inputs.closingCostRate, 2)} of loan`}
                  testid="cc-summary"
                />
              </div>
            </CardContent>
          </Card>

          {/* LOAN SIZING CONSTRAINTS */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
                Loan Sizing Constraints
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <ConstraintSlider
                  label="Max LTV (% of ARV)"
                  hint="Maximum loan-to-value — as a percentage of After Renovation Value. Default 55%."
                  value={inputs.maxLTV}
                  onChange={update('maxLTV')}
                  min={30}
                  max={90}
                  step={0.5}
                  testid="max-ltv-input"
                />
                <ConstraintSlider
                  label="Max LTC (% of Total Cost)"
                  hint="Maximum loan-to-cost — as a percentage of total project cost including CC & IR. Default 70%."
                  value={inputs.maxLTC}
                  onChange={update('maxLTC')}
                  min={40}
                  max={100}
                  step={0.5}
                  testid="max-ltc-input"
                />
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    label: 'Max Loan by LTV',
                    value: formatCurrency(c.maxByLTV),
                    detail: `${formatPercentage(inputs.maxLTV, 2)} × ${formatCurrency(inputs.arv)} ARV`,
                    binding: c.bindingConstraint === 'LTV',
                    testid: 'max-by-ltv'
                  },
                  {
                    label: 'Max Loan by LTC',
                    value: formatCurrency(c.maxByLTC),
                    detail: `${formatPercentage(inputs.maxLTC, 2)} × Total Cost (algebraic)`,
                    binding: c.bindingConstraint === 'LTC',
                    testid: 'max-by-ltc'
                  }
                ].map(({ label, value, detail, binding, testid }) => (
                  <div
                    key={label}
                    className={`p-4 rounded-md border transition-colors ${
                      binding
                        ? 'border-[#002F87]/30 bg-blue-50/50'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                    data-testid={testid}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#637888] uppercase tracking-wide">{label}</span>
                      {binding && (
                        <span className="text-xs font-bold text-[#002F87] bg-[#FFFF00] px-2 py-0.5 rounded uppercase tracking-wide">
                          Binding
                        </span>
                      )}
                    </div>
                    <p className={`font-mono-number text-lg font-semibold ${binding ? 'text-[#002F87]' : 'text-slate-700'}`}>
                      {value}
                    </p>
                    <p className="text-xs text-[#637888] mt-1">{detail}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* SOURCES & USES */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
                <Scale className="w-4 h-4" strokeWidth={1.5} />
                Sources &amp; Uses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">

              {/* Uses */}
              <div>
                <p className="text-xs text-[#637888] uppercase tracking-wide mb-2">Uses of Funds</p>
                <div className="space-y-2 text-sm">
                  {[
                    { desc: 'Land / Lot Acquisition', amount: inputs.landCost, testid: 'uses-land' },
                    { desc: 'Hard Construction Costs', amount: inputs.hardCosts, testid: 'uses-hard' },
                    { desc: 'Soft Costs (permits, architecture, etc.)', amount: inputs.softCosts, testid: 'uses-soft' },
                    { desc: `Closing Costs (${formatPercentage(inputs.closingCostRate, 2)} of loan)`, amount: c.closingCosts, testid: 'uses-cc' },
                    { desc: `Interest Reserve (${c.irMonths.toFixed(1)} mo @ ${formatPercentage(inputs.annualRate, 2)})`, amount: c.interestReserve, testid: 'uses-ir' },
                  ].map(({ desc, amount, testid }) => (
                    <div key={desc} className="flex justify-between" data-testid={testid}>
                      <span className="text-slate-600">{desc}</span>
                      <span className="font-mono-number text-slate-900 flex gap-4">
                        <span>{formatCurrency(amount)}</span>
                        <span className="text-[#637888] w-14 text-right">{pctOfTotal(amount, c.totalProjectCost)}</span>
                      </span>
                    </div>
                  ))}
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold text-[#002F87]">
                    <span>Total Uses</span>
                    <span className="font-mono-number flex gap-4">
                      <span data-testid="total-uses">{formatCurrency(c.totalProjectCost)}</span>
                      <span className="w-14 text-right">100.0%</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Sources */}
              <div>
                <p className="text-xs text-[#637888] uppercase tracking-wide mb-2">Sources of Funds</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between" data-testid="sources-initial-funding-row">
                    <span className="text-slate-600">
                      Initial Funding
                      <span className="ml-2 text-xs text-[#637888] font-mono-number">
                        ({formatCurrency(inputs.landValue)} × {formatPercentage(inputs.midpointPct, 2)} − {formatCurrency(inputs.currentDebt)} debt)
                      </span>
                    </span>
                    <span className="font-mono-number text-slate-900 flex gap-4">
                      <span data-testid="sources-initial-funding-amount">{formatCurrency(c.initialFunding)}</span>
                      <span className="text-[#637888] w-14 text-right">{pctOfTotal(c.initialFunding, c.totalProjectCost)}</span>
                    </span>
                  </div>
                  <div className="flex justify-between" data-testid="sources-holdback-row">
                    <span className="text-slate-600">
                      Construction Holdback
                      <span className="ml-2 text-xs text-[#637888] font-mono-number">(Total Loan − Initial Funding)</span>
                    </span>
                    <span className="font-mono-number text-slate-900 flex gap-4">
                      <span data-testid="sources-holdback-amount">{formatCurrency(c.constructionHoldback)}</span>
                      <span className="text-[#637888] w-14 text-right">{pctOfTotal(c.constructionHoldback, c.totalProjectCost)}</span>
                    </span>
                  </div>
                  <div className="flex justify-between text-xs italic text-[#637888] pl-4" data-testid="sources-total-loan-row">
                    <span>Total Construction Loan</span>
                    <span className="font-mono-number flex gap-4">
                      <span data-testid="sources-loan-amount">{formatCurrency(c.loanAmount)}</span>
                      <span className="w-14 text-right">{pctOfTotal(c.loanAmount, c.totalProjectCost)}</span>
                    </span>
                  </div>
                  <div className="flex justify-between" data-testid="sources-equity-row">
                    <span className="text-slate-600">Sponsor / Developer Equity</span>
                    <span className="font-mono-number text-slate-900 flex gap-4">
                      <span data-testid="sources-equity-amount">{formatCurrency(c.equity)}</span>
                      <span className="text-[#637888] w-14 text-right">{pctOfTotal(c.equity, c.totalProjectCost)}</span>
                    </span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold text-[#002F87]">
                    <span>Total Sources</span>
                    <span className="font-mono-number flex gap-4">
                      <span data-testid="total-sources">{formatCurrency(c.totalProjectCost)}</span>
                      <span className="w-14 text-right">100.0%</span>
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Summary — Right Column */}
        <div className="lg:col-span-2">
          <div className="sticky top-6">
            <Card className="border border-slate-200 shadow-lg overflow-hidden" data-testid="loan-summary-card">
              {/* Yellow accent line */}
              <div className="h-1 bg-[#FFFF00]" />

              {/* Summary Header */}
              <div className="bg-[#002F87] text-white px-6 py-4">
                <h2 className="font-heading font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                  <CalcIcon className="w-4 h-4" strokeWidth={1.5} />
                  Construction Loan Summary
                </h2>
                <p className="text-xs text-white/70 mt-1">Spec Construction Loan Sizer</p>
              </div>

              <CardContent className="p-6 space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 p-4 bg-[#002F87] rounded-md">
                    <p className="text-xs text-white/80 uppercase tracking-wide mb-1">Loan Amount</p>
                    <p className="font-mono-number text-2xl font-bold text-white animate-number" data-testid="loan-amount-summary">
                      {formatCurrency(c.loanAmount)}
                    </p>
                    <p className="text-xs mt-2">
                      <span
                        className="font-bold bg-[#FFFF00] text-[#002F87] px-2 py-0.5 rounded uppercase tracking-wide"
                        data-testid="binding-constraint"
                      >
                        {c.bindingConstraint} Binding
                      </span>
                      <span className="text-white/60 ml-2">constraint</span>
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Initial Funding</p>
                    <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number" data-testid="summary-initial-funding">
                      {formatCurrency(c.initialFunding)}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Holdback</p>
                    <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number" data-testid="summary-construction-holdback">
                      {formatCurrency(c.constructionHoldback)}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Monthly IO</p>
                    <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number" data-testid="summary-monthly-io">
                      {formatCurrency(c.monthlyIO)}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-xs text-[#637888] uppercase tracking-wide mb-1">Equity Required</p>
                    <p className="font-mono-number text-lg font-semibold text-[#002F87] animate-number" data-testid="summary-equity">
                      {formatCurrency(c.equity)}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Sizing Detail */}
                <div>
                  <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                    Loan Sizing
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Max by LTV ({formatPercentage(inputs.maxLTV, 2)})</span>
                      <span
                        className={`font-mono-number ${c.bindingConstraint === 'LTV' ? 'font-bold text-[#002F87]' : 'text-slate-900'}`}
                        data-testid="summary-max-ltv"
                      >
                        {formatCurrency(c.maxByLTV)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Max by LTC ({formatPercentage(inputs.maxLTC, 2)})</span>
                      <span
                        className={`font-mono-number ${c.bindingConstraint === 'LTC' ? 'font-bold text-[#002F87]' : 'text-slate-900'}`}
                        data-testid="summary-max-ltc"
                      >
                        {formatCurrency(c.maxByLTC)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Interest Reserve ({c.irMonths.toFixed(1)} mo)</span>
                      <span className="font-mono-number text-slate-900" data-testid="summary-interest-reserve">
                        {formatCurrency(c.interestReserve)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Closing Costs ({formatPercentage(inputs.closingCostRate, 2)})</span>
                      <span className="font-mono-number text-slate-900" data-testid="summary-closing-costs">
                        {formatCurrency(c.closingCosts)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total Interest (full term)</span>
                      <span className="font-mono-number text-slate-900" data-testid="summary-total-interest">
                        {formatCurrency(c.totalInterestFullTerm)}
                      </span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between font-semibold text-[#002F87]">
                      <span>Total Project Cost</span>
                      <span className="font-mono-number" data-testid="summary-total-cost">
                        {formatCurrency(c.totalProjectCost)}
                      </span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Ratios */}
                <div>
                  <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                    Resulting Ratios
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Actual LTV (of ARV)</span>
                      <span
                        className={`font-mono-number font-semibold ${c.actualLTV > inputs.maxLTV + 0.1 ? 'text-red-600' : 'text-[#002F87]'}`}
                        data-testid="summary-actual-ltv"
                      >
                        {formatPercentage(c.actualLTV, 2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Actual LTC (of Total Cost)</span>
                      <span
                        className={`font-mono-number font-semibold ${c.actualLTC > inputs.maxLTC + 0.1 ? 'text-red-600' : 'text-[#002F87]'}`}
                        data-testid="summary-actual-ltc"
                      >
                        {formatPercentage(c.actualLTC, 2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* PDF Download */}
                <PDFDownloadLink
                  document={<ConstructionSizerPDF data={c} inputs={inputs} loanOfficer={loanOfficer} />}
                  fileName={`construction-loan-summary-${inputs.projectName?.replace(/\s+/g, '-') || 'project'}-${new Date().toISOString().split('T')[0]}.pdf`}
                  className="block"
                >
                  {({ loading }) => (
                    <Button
                      data-testid="download-sizer-pdf-btn"
                      className="w-full h-12 bg-[#FFFF00] hover:bg-[#E6E600] text-black font-heading font-semibold uppercase tracking-wide"
                      disabled={loading || c.loanAmount <= 0}
                    >
                      {loading ? (
                        <>Generating PDF...</>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" strokeWidth={2} />
                          Download PDF Summary
                        </>
                      )}
                    </Button>
                  )}
                </PDFDownloadLink>

                {/* Scenario Tools */}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowSave(true)}
                    data-testid="save-btn"
                    className="text-xs text-[#637888] hover:text-[#002F87]"
                  >
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleLoadOpen}
                    data-testid="load-btn"
                    className="text-xs text-[#637888] hover:text-[#002F87]"
                  >
                    <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
                    Load
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    data-testid="reset-btn"
                    className="text-xs text-[#637888] hover:text-[#002F87]"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

      </div>

      {/* SAVE MODAL */}
      {showSave && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="save-modal">
          <div className="bg-white rounded-md shadow-xl w-full max-w-md border border-slate-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide">Save Scenario</h3>
              <button onClick={() => setShowSave(false)} className="text-slate-400 hover:text-slate-600">
                <X size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <FieldLabel>Scenario Name</FieldLabel>
                <Input
                  type="text"
                  value={scenarioName}
                  onChange={(e) => setScenarioName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                  placeholder="e.g., 123 Oak St — Base Case"
                  data-testid="scenario-name-input"
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowSave(false)}
                  data-testid="save-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || !scenarioName.trim()}
                  className="bg-[#002F87] hover:bg-[#00256B] text-white"
                  data-testid="save-confirm-btn"
                >
                  {saving ? 'Saving...' : 'Save Scenario'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LOAD MODAL */}
      {showLoad && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="load-modal">
          <div className="bg-white rounded-md shadow-xl w-full max-w-md border border-slate-200 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide">Load Scenario</h3>
              <button onClick={() => setShowLoad(false)} className="text-slate-400 hover:text-slate-600">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingScenarios ? (
                <div className="p-8 text-center text-sm text-[#637888]">Loading...</div>
              ) : savedScenarios.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#637888]">No saved scenarios yet.</div>
              ) : (
                <div className="p-3 space-y-1">
                  {savedScenarios.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handleLoadScenario(s)}
                      data-testid={`scenario-${s.id}`}
                      className="w-full text-left px-4 py-3 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all group"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-sm font-medium text-slate-800">{s.name}</div>
                          <div className="text-xs text-[#637888] mt-0.5 font-mono-number">
                            ARV {formatCurrency(s.arv)} &nbsp;·&nbsp; Rate {s.annual_rate}% &nbsp;·&nbsp; {s.term_months}mo
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteScenario(s.id, e)}
                          data-testid={`delete-scenario-${s.id}`}
                          className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all p-1 mt-0.5"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
